import React, { MouseEvent, useContext, useState } from 'react';
import styled from 'astroturf/react';
import { Suggestion } from 'js/social/shared/types';
import AvatarBase from 'js/social/shared/Avatar';
import { Body } from 'js/design_system/Body';
import CloseIcon from 'js/svg/colorless/close.svg';
import { FollowActionButton } from 'js/social/shared/FollowActionButton';
import { ignoreSuggestion } from 'js/profiles/api/suggested_api';
import { Spinner } from 'js/social/shared/CreateSocialItem/Spinner';
import { createUserFollowing } from 'js/social/api/user_following_api';
import { SuggestionReason as SuggestionReasonBase } from 'js/social/suggestions/SuggestionReason';
import { NetworkContext } from 'js/profiles/types';

const Header = styled.div<{ small: boolean }>`
  display: flex;
  justify-content: space-between;

  &.small {
    margin-bottom: 4px;
  }
`;

const Avatar = styled(AvatarBase)`
  --size: 48px;
  flex-shrink: 0;
`;

const ProfileText = styled.div`
  color: #222233;
`;

const IgnoreButton = styled.div`
  flex-shrink: 0;
`;

const NameText = styled(Body)`
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis;
`;

const AffiliationText = styled(Body)<{ small: boolean }>`
  @import 'stylesheets/design_system/_util.scss';
  @include text-ellipsis(1, 18px);

  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: $ds-gray-dark;

  &.small {
    @include text-ellipsis(2, 18px);
    text-wrap: wrap;
  }
`;

const SuggestionReason = styled(SuggestionReasonBase)<{ small: boolean }>`
  flex-grow: 1;

  &.small {
    margin-bottom: 4px;
  }
`;

const FollowButton = styled(FollowActionButton)`
  margin-top: auto;
  text-align: center;

  &:not(:disabled) {
    border: 1px solid #9bb5e0;

    &:hover {
      border: 1px solid #9bb5e0;
    }
  }

  > span {
    width: 100%;
  }
`;

const Container = styled.a<{ small: boolean; ignored: boolean }>`
  height: 216px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 4px;
  border: 1px solid $ds-gray-light;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.08);
  overflow-wrap: anywhere;

  &.ignored {
    align-items: center;
    justify-content: center;
  }

  &.small {
    gap: 4px;
    height: unset;
    min-height: 202px;
  }

  &:hover ${NameText} {
    text-decoration: underline;
  }
`;

type Props = {
  small?: boolean;
  suggestion: Suggestion;
  suggestionSource: string;
  followSource: string;
  onIgnore?: () => void;
  alwaysShowFollowButton?: boolean;
};

export const SuggestionCard: React.FC<Props> = ({
  small = false,
  suggestion,
  suggestionSource,
  followSource,
  onIgnore,
  alwaysShowFollowButton = false,
}) => {
  const { follow: networkFollow } = useContext(NetworkContext);
  const [follows, setFollows] = useState(suggestion.user.followed || false);
  const [ignored, setIgnored] = useState(false);
  const user = suggestion.user;

  const follow = async () => {
    await createUserFollowing(user.id, followSource, {
      suggestion_reason: suggestionSource,
    });
    networkFollow(user.id, true);
    setFollows(true);
  };

  const ignore = async (event: MouseEvent) => {
    event.preventDefault();
    setIgnored(true);
    await ignoreSuggestion(user.id, suggestionSource);
    if (onIgnore) onIgnore();
  };

  return (
    <Container
      small={small}
      href={ignored ? undefined : user.url}
      ignored={ignored}
      onDragStart={(event) => event.preventDefault()}
      onDragEnd={(event) => event.preventDefault()}
    >
      {ignored ? (
        <Spinner />
      ) : (
        <>
          <Header small={small}>
            <Avatar fullName={user.display_name ?? ''} photoUrl={user.photo} />
            {!follows && onIgnore && (
              <IgnoreButton onClick={ignore}>
                <CloseIcon size={20} color="#B1B1BA" />
              </IgnoreButton>
            )}
          </Header>

          <ProfileText>
            <NameText size="sm" bold>
              {user.display_name}
            </NameText>
            <AffiliationText small={small}>
              {user.department?.university?.name}&nbsp;
            </AffiliationText>
          </ProfileText>

          <SuggestionReason
            small={small}
            source={suggestionSource}
            metadata={suggestion.metadata}
          />

          <FollowButton
            size="xs"
            variant="text"
            userId={user.id}
            follows={follows}
            onFollow={follow}
            alwaysShowFollowButton={alwaysShowFollowButton}
          >
            Follow
          </FollowButton>
        </>
      )}
    </Container>
  );
};
